// Volume Balance Analysis for Muscle Group Distribution

export interface MuscleGroupVolume {
  muscleGroup: string;
  sets: number;
  totalVolume: number; // sets * reps * weight
  percentage: number;
  exercises: string[];
}

export interface VolumeImbalance {
  type: "push-pull" | "upper-lower" | "bilateral" | "agonist-antagonist";
  description: string;
  severity: "low" | "medium" | "high";
  ratio: number;
  recommendation: string;
}

export interface VolumeAnalysis {
  muscleGroups: MuscleGroupVolume[];
  totalSets: number;
  totalVolume: number;
  imbalances: VolumeImbalance[];
  weeklyAverage: {
    sets: number;
    volume: number;
  };
}

export interface VolumeTrendPoint {
  date: string;
  label: string;
  sets: number;
  volume: number;
  workouts: number;
}

// Muscle group categories for balance analysis
const PUSH_MUSCLES = ["chest", "shoulders", "triceps", "front delts"];
const PULL_MUSCLES = ["back", "biceps", "rear delts", "traps", "lats"];
const UPPER_MUSCLES = ["chest", "back", "shoulders", "biceps", "triceps", "traps", "lats", "forearms", "front delts", "rear delts"];
const LOWER_MUSCLES = ["quads", "hamstrings", "glutes", "calves", "hip flexors", "adductors", "abductors"];
const CORE_MUSCLES = ["core", "abs", "obliques", "lower back"];

// Agonist-antagonist pairs
const MUSCLE_PAIRS: [string, string][] = [
  ["chest", "back"],
  ["biceps", "triceps"],
  ["quads", "hamstrings"],
  ["shoulders", "back"],
];

// Ideal ratios for balance
const IDEAL_RATIOS = {
  "push-pull": { min: 0.8, max: 1.2, ideal: 1.0 },
  "upper-lower": { min: 0.8, max: 1.5, ideal: 1.0 },
  "agonist-antagonist": { min: 0.8, max: 1.2, ideal: 1.0 },
};

export function analyzeVolumeBalance(
  setLogs: Array<{
    exercise_id: string;
    exercise_name: string;
    muscle_groups: { primary: string[]; secondary: string[] } | null;
    weight: number | null;
    reps_completed: number | null;
    workout_date: string;
  }>,
  weeksAnalyzed: number = 4
): VolumeAnalysis {
  const muscleGroupMap = new Map<string, {
    sets: number;
    volume: number;
    exercises: Set<string>;
  }>();

  let totalSets = 0;
  let totalVolume = 0;

  // Aggregate volume by muscle group
  for (const log of setLogs) {
    const weight = log.weight || 0;
    const reps = log.reps_completed || 0;
    const volume = weight * reps;
    
    totalSets += 1;
    totalVolume += volume;

    const primaryMuscles = log.muscle_groups?.primary || [];
    const secondaryMuscles = log.muscle_groups?.secondary || [];

    // Primary muscles get full credit
    for (const muscle of primaryMuscles) {
      const normalized = muscle.toLowerCase();
      if (!muscleGroupMap.has(normalized)) {
        muscleGroupMap.set(normalized, { sets: 0, volume: 0, exercises: new Set() });
      }
      const data = muscleGroupMap.get(normalized)!;
      data.sets += 1;
      data.volume += volume;
      data.exercises.add(log.exercise_name);
    }

    // Secondary muscles get partial credit (50%)
    for (const muscle of secondaryMuscles) {
      const normalized = muscle.toLowerCase();
      if (!muscleGroupMap.has(normalized)) {
        muscleGroupMap.set(normalized, { sets: 0, volume: 0, exercises: new Set() });
      }
      const data = muscleGroupMap.get(normalized)!;
      data.sets += 0.5;
      data.volume += volume * 0.5;
      data.exercises.add(log.exercise_name);
    }
  }

  // Convert to array and calculate percentages
  const muscleGroups: MuscleGroupVolume[] = Array.from(muscleGroupMap.entries())
    .map(([muscleGroup, data]) => ({
      muscleGroup,
      sets: Math.round(data.sets),
      totalVolume: Math.round(data.volume),
      percentage: totalSets > 0 ? (data.sets / totalSets) * 100 : 0,
      exercises: Array.from(data.exercises),
    }))
    .sort((a, b) => b.sets - a.sets);

  // Analyze imbalances
  const imbalances = analyzeImbalances(muscleGroupMap);

  return {
    muscleGroups,
    totalSets,
    totalVolume: Math.round(totalVolume),
    imbalances,
    weeklyAverage: {
      sets: weeksAnalyzed > 0 ? Math.round(totalSets / weeksAnalyzed) : 0,
      volume: weeksAnalyzed > 0 ? Math.round(totalVolume / weeksAnalyzed) : 0,
    },
  };
}

function analyzeImbalances(
  muscleGroupMap: Map<string, { sets: number; volume: number; exercises: Set<string> }>
): VolumeImbalance[] {
  const imbalances: VolumeImbalance[] = [];

  const getSetsForGroups = (groups: string[]): number => {
    return groups.reduce((sum, g) => {
      const data = muscleGroupMap.get(g.toLowerCase());
      return sum + (data?.sets || 0);
    }, 0);
  };

  // Push vs Pull analysis
  const pushSets = getSetsForGroups(PUSH_MUSCLES);
  const pullSets = getSetsForGroups(PULL_MUSCLES);
  
  if (pushSets > 0 || pullSets > 0) {
    const ratio = pullSets > 0 ? pushSets / pullSets : pushSets > 0 ? 2 : 1;
    
    if (ratio < IDEAL_RATIOS["push-pull"].min || ratio > IDEAL_RATIOS["push-pull"].max) {
      const severity = ratio < 0.6 || ratio > 1.6 ? "high" : ratio < 0.7 || ratio > 1.4 ? "medium" : "low";
      imbalances.push({
        type: "push-pull",
        description: ratio > 1 
          ? `Push exercises exceed pull by ${((ratio - 1) * 100).toFixed(0)}%`
          : `Pull exercises exceed push by ${((1 / ratio - 1) * 100).toFixed(0)}%`,
        severity,
        ratio: Number(ratio.toFixed(2)),
        recommendation: ratio > 1 
          ? "Add more pulling exercises (rows, pull-ups, face pulls)"
          : "Add more pushing exercises (presses, push-ups, dips)",
      });
    }
  }

  // Upper vs Lower analysis
  const upperSets = getSetsForGroups(UPPER_MUSCLES);
  const lowerSets = getSetsForGroups(LOWER_MUSCLES);
  
  if (upperSets > 0 || lowerSets > 0) {
    const ratio = lowerSets > 0 ? upperSets / lowerSets : upperSets > 0 ? 3 : 1;
    
    if (ratio > IDEAL_RATIOS["upper-lower"].max || ratio < IDEAL_RATIOS["upper-lower"].min) {
      const severity = ratio > 2.5 || ratio < 0.5 ? "high" : ratio > 2 || ratio < 0.6 ? "medium" : "low";
      imbalances.push({
        type: "upper-lower",
        description: ratio > 1.5
          ? `Upper body training is ${((ratio - 1) * 100).toFixed(0)}% more than lower body`
          : `Lower body training exceeds upper by ${((1 / ratio - 1) * 100).toFixed(0)}%`,
        severity,
        ratio: Number(ratio.toFixed(2)),
        recommendation: ratio > 1.5
          ? "Add more leg exercises (squats, lunges, deadlifts)"
          : "Balance with more upper body work",
      });
    }
  }

  // Agonist-antagonist analysis
  for (const [agonist, antagonist] of MUSCLE_PAIRS) {
    const agonistData = muscleGroupMap.get(agonist);
    const antagonistData = muscleGroupMap.get(antagonist);
    
    if (agonistData && antagonistData && (agonistData.sets > 2 || antagonistData.sets > 2)) {
      const ratio = antagonistData.sets > 0 
        ? agonistData.sets / antagonistData.sets 
        : agonistData.sets > 0 ? 2 : 1;
      
      if (ratio < 0.6 || ratio > 1.6) {
        const dominant = ratio > 1 ? agonist : antagonist;
        const weaker = ratio > 1 ? antagonist : agonist;
        
        imbalances.push({
          type: "agonist-antagonist",
          description: `${capitalize(dominant)} training exceeds ${weaker} significantly`,
          severity: ratio < 0.5 || ratio > 2 ? "high" : "medium",
          ratio: Number(ratio.toFixed(2)),
          recommendation: `Add more ${weaker} exercises to balance with ${dominant}`,
        });
      }
    }
  }

  // Check for neglected muscle groups
  const commonMuscles = ["chest", "back", "shoulders", "quads", "hamstrings", "glutes", "core"];
  for (const muscle of commonMuscles) {
    const data = muscleGroupMap.get(muscle);
    if (!data || data.sets < 2) {
      imbalances.push({
        type: "bilateral",
        description: `${capitalize(muscle)} appears undertrained or neglected`,
        severity: "medium",
        ratio: 0,
        recommendation: `Consider adding ${muscle} exercises to your program`,
      });
    }
  }

  return imbalances.slice(0, 5); // Return top 5 most relevant imbalances
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Color mapping for muscle groups
export const MUSCLE_GROUP_COLORS: Record<string, string> = {
  chest: "hsl(0, 70%, 60%)",
  back: "hsl(210, 70%, 55%)",
  shoulders: "hsl(270, 60%, 60%)",
  biceps: "hsl(140, 60%, 50%)",
  triceps: "hsl(45, 80%, 55%)",
  quads: "hsl(25, 80%, 55%)",
  hamstrings: "hsl(330, 60%, 60%)",
  glutes: "hsl(250, 60%, 60%)",
  calves: "hsl(175, 60%, 50%)",
  core: "hsl(190, 70%, 50%)",
  forearms: "hsl(80, 50%, 50%)",
  traps: "hsl(35, 70%, 55%)",
  lats: "hsl(220, 60%, 55%)",
  abs: "hsl(195, 65%, 50%)",
  obliques: "hsl(350, 60%, 60%)",
};

export function getMuscleGroupColor(muscleGroup: string): string {
  return MUSCLE_GROUP_COLORS[muscleGroup.toLowerCase()] || "hsl(220, 10%, 50%)";
}

// Calculate volume trends over time (weekly or monthly)
export function calculateVolumeTrends(
  setLogs: Array<{
    weight: number | null;
    reps_completed: number | null;
    workout_date: string;
    workout_id: string;
  }>,
  granularity: "weekly" | "monthly" = "weekly",
  weeksBack: number = 12
): VolumeTrendPoint[] {
  const now = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - weeksBack * 7);

  // Group data by period
  const periodMap = new Map<string, { sets: number; volume: number; workoutIds: Set<string> }>();

  for (const log of setLogs) {
    const logDate = new Date(log.workout_date);
    if (logDate < startDate) continue;

    const periodKey = granularity === "weekly" 
      ? getWeekKey(logDate)
      : getMonthKey(logDate);

    if (!periodMap.has(periodKey)) {
      periodMap.set(periodKey, { sets: 0, volume: 0, workoutIds: new Set() });
    }

    const data = periodMap.get(periodKey)!;
    data.sets += 1;
    data.volume += (log.weight || 0) * (log.reps_completed || 0);
    data.workoutIds.add(log.workout_id);
  }

  // Generate all periods in range
  const periods: VolumeTrendPoint[] = [];
  const current = new Date(startDate);

  while (current <= now) {
    const periodKey = granularity === "weekly"
      ? getWeekKey(current)
      : getMonthKey(current);

    const data = periodMap.get(periodKey);
    const label = granularity === "weekly"
      ? formatWeekLabel(current)
      : formatMonthLabel(current);

    // Only add if we haven't already added this period
    if (!periods.find(p => p.date === periodKey)) {
      periods.push({
        date: periodKey,
        label,
        sets: data?.sets || 0,
        volume: Math.round(data?.volume || 0),
        workouts: data?.workoutIds.size || 0,
      });
    }

    // Move to next period
    if (granularity === "weekly") {
      current.setDate(current.getDate() + 7);
    } else {
      current.setMonth(current.getMonth() + 1);
    }
  }

  return periods;
}

function getWeekKey(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // Get Monday of the week
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split("T")[0];
}

function getMonthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function formatWeekLabel(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatMonthLabel(date: Date): string {
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}
